import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Leaf, Sparkles, Heart, ShoppingBag } from 'lucide-react';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [featuredTeas, setFeaturedTeas] = useState([]);

  useEffect(() => {
    fetchFeaturedTeas();
  }, []);

  const fetchFeaturedTeas = async () => {
    try {
      const response = await axios.get(`${API}/teas?featured=true`);
      setFeaturedTeas(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching featured teas:', error);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#cc5500' }}>
      <Navbar />

      {/* Hero Section */}
      <section 
        className="relative overflow-hidden" 
        style={{ 
          backgroundImage: 'linear-gradient(rgba(204, 85, 0, 0.85), rgba(204, 85, 0, 0.85)), url(https://images.unsplash.com/photo-1571934811356-5cc061b6821f?q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }} 
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center max-w-4xl mx-auto fade-in">
            <div className="flex justify-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_34476667-a3f6-4f7e-89c3-0dc66402e9b8/artifacts/9u4cd63c_JFRAI%20Family%20Rooted%20logo.png" 
                alt="JFRAI Family Rooted Logo" 
                className="w-48 h-48 object-contain"
              />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="hero-title">
              Welcome to JFRAI Family Flavorful Brews & Tings
            </h1>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-cream-100 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Nature's Healing,
              <br />
              <span className="text-cream-100">Crafted with Care</span>
            </h2>
            <p className="text-lg sm:text-xl text-cream-100 mb-8 max-w-2xl mx-auto" data-testid="hero-description">
              Discover over 150 all-natural herbal teas, carefully curated to support your wellness journey. From ancient remedies to modern blends.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-sage-600 hover:bg-sage-700 text-lg px-8 py-6 rounded-full"
                onClick={() => navigate('/shop')}
                data-testid="hero-shop-button"
              >
                <ShoppingBag className="mr-2 w-5 h-5" />
                Explore Our Collection
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-sage-600 text-sage-700 hover:bg-sage-50 text-lg px-8 py-6 rounded-full"
                onClick={() => navigate('/blend')}
                data-testid="hero-blend-button"
              >
                <Sparkles className="mr-2 w-5 h-5" />
                Create Custom Blend
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Teas */}
      <section 
        className="py-20 relative" 
        style={{ 
          backgroundImage: 'linear-gradient(rgba(204, 85, 0, 0.90), rgba(204, 85, 0, 0.90)), url(https://images.unsplash.com/photo-1563822249366-3efb23b8e0c9?q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }} 
        data-testid="featured-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Featured Blends
            </h2>
            <p className="text-lg text-cream-100">Handpicked selections for your well-being</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 stagger-fade-in">
            {featuredTeas.map((tea) => (
              <div
                key={tea.id}
                className="tea-card bg-cream-50 rounded-2xl overflow-hidden shadow-lg cursor-pointer"
                onClick={() => navigate(`/tea/${tea.id}`)}
                data-testid={`featured-tea-${tea.id}`}
              >
                <div className="h-64 overflow-hidden">
                  <img
                    src={tea.image_url}
                    alt={tea.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-semibold text-sage-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {tea.name}
                  </h3>
                  <p className="text-sage-600 mb-4 line-clamp-2">{tea.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-terracotta-600">${tea.price.toFixed(2)}</span>
                    <span className="text-sm text-sage-500">{tea.category}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section 
        className="py-20 relative" 
        style={{ 
          backgroundImage: 'linear-gradient(rgba(179, 74, 0, 0.92), rgba(179, 74, 0, 0.92)), url(https://images.unsplash.com/photo-1596398998950-34508ed6d173?q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }} 
        data-testid="benefits-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-sage-600 text-white rounded-full mb-4">
                <Leaf className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                100% Natural
              </h3>
              <p className="text-cream-100">
                Pure, organic ingredients sourced from trusted growers. No artificial additives or preservatives.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-terracotta-600 text-white rounded-full mb-4">
                <Heart className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                Wellness Focused
              </h3>
              <p className="text-cream-100">
                Each blend is carefully crafted to address specific health concerns and support your well-being.
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-sage-600 text-white rounded-full mb-4">
                <Sparkles className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                Custom Blends
              </h3>
              <p className="text-cream-100">
                Create your perfect blend with AI-powered recommendations based on your health needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Botanical Showcase */}
      <section className="relative h-96 overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1586161665517-0325578c2784?q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.8)'
          }}
        />
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Pure. Natural. Healing.
            </h2>
            <p className="text-xl opacity-90">Ancient wisdom meets modern wellness</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-sage-700 to-sage-800 text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Start Your Wellness Journey Today
          </h2>
          <p className="text-xl text-sage-100 mb-8">
            Join thousands who have discovered the power of herbal healing
          </p>
          <Button
            size="lg"
            className="bg-terracotta-600 hover:bg-terracotta-700 text-lg px-8 py-6 rounded-full"
            onClick={() => navigate('/shop')}
            data-testid="cta-shop-button"
          >
            Browse Our Collection
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-sage-900 text-sage-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Leaf className="w-8 h-8 text-sage-400" />
            </div>
            <p className="text-lg font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              JFRAI Family Flavorful Brews & Tings
            </p>
            <p className="text-sage-400 text-sm">
              © 2025 JFRAI. All rights reserved. Natural healing through ancestral wisdom.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
